import { useCallback } from 'react';
import { useEnrollment } from '../context';
import {
  buildCredentialCreationOptions,
  extractCredentialResponse,
  getWebAuthnErrorMessage,
} from '../services/webauthn';
import type { CredentialResponse } from '../services/webauthn';

function getError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (typeof error === 'object' && error !== null) {
    const converted = new Error(
      'message' in error && typeof error.message === 'string' ? error.message : '',
    );
    if ('name' in error && typeof error.name === 'string') {
      converted.name = error.name;
    }
    return converted;
  }

  return new Error('');
}

export function useWebAuthnRegistration(): () => Promise<CredentialResponse | null | undefined> {
  const { data, dispatch } = useEnrollment();

  return useCallback(async (): Promise<CredentialResponse | null | undefined> => {
    if (!window.PublicKeyCredential) {
      dispatch({
        type: 'SET_ERROR',
        errorMessage: 'Seu navegador não suporta autenticação biométrica.',
      });
      return null;
    }

    const registrationOptions = data.registrationOptions;
    if (!registrationOptions) {
      dispatch({ type: 'SET_ERROR', errorMessage: 'Opções de registro não disponíveis.' });
      return null;
    }

    dispatch({ type: 'START_AUTHENTICATION' });

    try {
      const publicKeyOptions = buildCredentialCreationOptions(registrationOptions);
      const credential = await navigator.credentials.create({ publicKey: publicKeyOptions });

      if (!credential) {
        throw new Error('WebAuthn registration returned no credential');
      }

      return extractCredentialResponse(credential as PublicKeyCredential);
    } catch (caughtError: unknown) {
      const error = getError(caughtError);
      const errorMessage = getWebAuthnErrorMessage(error);
      const isNonRetryable = error.name === 'NotSupportedError'
        || error.name === 'SecurityError'
        || error.name === 'InvalidStateError';

      if (isNonRetryable) {
        dispatch({ type: 'SET_ERROR', errorMessage });
      } else {
        dispatch({ type: 'SET_AUTH_ERROR', errorMessage });
      }
      return null;
    }
  }, [data.registrationOptions, dispatch]);
}
